import type { CSSProperties, JSX, ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import { ActionIcon, Menu, Table, Text } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api';
import { TABLE_TEXTS } from '../format';
import { useContextMenu } from './ContextMenu';
import { DetailModal, type DetailMode } from './DetailModal';
import type { DetailChanges, DetailField } from './detailForm';
import { useLazyCount } from './PageScroll';
import { MIN_COLUMN_WIDTH, type TableWidths, loadTableWidths, resizedWidth, saveTableWidths, stepWidth } from './columnWidths';
import { LONG_PRESS_MS, confirmThen, moveSelection, movedTooFar, returnFocus, rowKeyAction, rowMenuEntries } from './tableKeys';

export interface LibraryColumn<T> {
  key: string;
  /** Heading, and the name of its grip for screen readers. */
  title: string;
  /** Shown in the heading instead of the title (e.g. a checkbox). */
  header?: ReactNode;
  hint?: string;
  defaultWidth: number;
  minWidth?: number;
  align?: 'left' | 'right';
  render: (row: T) => ReactNode;
}

const MENU_COLUMN = 36;

/**
 * The table of a tab under Verwaltung › Bibliotheken (AP-128). A click
 * selects a row, the arrows move the selection, a double click or Enter opens
 * the entry in a popup; the right mouse button, the ⋯ button, the
 * context-menu key, Shift + F10 or a long press bring up its menu. Changing
 * and removing happen there or in the popup only - never on a single click -
 * and only with library.manage (ADR 0020). Columns are as wide as the user
 * drags them, kept per table and user in this browser.
 */
export function LibraryTable<T>({
  tableId, label, columns, rows, rowKey, rowLabel, rowStyle, canManage, fields, detailTitle, detailExtra, edit, remove, extraMenu,
  maxHeight = 560, lazyStep = 150, resetKey,
}: {
  /** Names the stored widths; stays the same across releases. */
  tableId: string;
  label: string;
  columns: readonly LibraryColumn<T>[];
  rows: readonly T[];
  rowKey: (row: T) => string;
  rowLabel: (row: T) => string;
  rowStyle?: (row: T) => CSSProperties | undefined;
  canManage: boolean;
  /** Everything about the entry for the popup; a field with `edit` can be changed there. */
  fields: (row: T) => DetailField[];
  detailTitle?: (row: T) => string;
  detailExtra?: (row: T) => ReactNode;
  /** The tab's way of writing an entry; without it the popup only shows. */
  edit?: (row: T, changes: DetailChanges) => Promise<void>;
  remove?: { label?: (row: T) => string; question: (row: T) => string; run: (row: T) => void };
  /** Further entries for the administration, between Bearbeiten and Löschen; they call `close` themselves. */
  extraMenu?: (row: T, close: () => void) => ReactNode;
  maxHeight?: number;
  lazyStep?: number;
  /** Starts the lazy list over (another filter, another library). */
  resetKey?: unknown;
}): JSX.Element {
  const userId = useQuery({ queryKey: ['me'], queryFn: api.me }).data?.id;
  const [widths, setWidths] = useState<TableWidths>(() => loadTableWidths(tableId, userId, columns));
  // The user may arrive after the first render; their widths then replace the
  // defaults. Not on every new `columns` array: a column missing from the
  // widths falls back to its default in widthOf.
  useEffect(() => setWidths(loadTableWidths(tableId, userId, columns)), [tableId, userId]);
  const widthOf = (c: LibraryColumn<T>): number => widths[c.key] ?? c.defaultWidth;
  const resize = (key: string, width: number): void => {
    setWidths((previous) => {
      const next = { ...previous, [key]: width };
      saveTableWidths(tableId, userId, next);
      return next;
    });
  };

  const [selected, setSelected] = useState<string | null>(null);
  const [detail, setDetail] = useState<{ key: string; mode: DetailMode } | null>(null);
  // Set at once, not on the next render: the menu closing must know a popup took over.
  const detailOpen = useRef(false);
  const rowEls = useRef(new Map<string, HTMLTableRowElement>());
  const press = useRef<{ timer: ReturnType<typeof setTimeout>; x: number; y: number } | null>(null);

  const lazy = useLazyCount(rows.length, lazyStep, resetKey);
  const visible = rows.slice(0, lazy.shown);
  const index = visible.findIndex((r) => rowKey(r) === selected);
  // Roving tabindex: one row is in the tab order - the selected one, else the first.
  const tabRow = index >= 0 ? selected : visible[0] ? rowKey(visible[0]) : null;
  const editable = (row: T): boolean => edit !== undefined && fields(row).some((f) => f.edit);

  const openDetail = (row: T, mode: DetailMode): void => {
    detailOpen.current = true;
    setSelected(rowKey(row));
    setDetail({ key: rowKey(row), mode: mode === 'edit' && canManage && editable(row) ? 'edit' : 'view' });
  };
  const closeDetail = (): void => {
    const key = detail?.key;
    detailOpen.current = false;
    setDetail(null);
    if (key) returnFocus(() => rowEls.current.get(key));
  };

  const menu = useContextMenu<T>((row, close) => {
    const run = (f: () => void) => () => { f(); close(); };
    const entries = rowMenuEntries(canManage, { editable: editable(row), removable: remove !== undefined });
    const name = rowLabel(row);
    return (
      <>
        <Menu.Label>{name.length > 40 ? `${name.slice(0, 40)} …` : name}</Menu.Label>
        <Menu.Item onClick={run(() => openDetail(row, 'view'))}>{TABLE_TEXTS.view}</Menu.Item>
        {entries.includes('edit') && <Menu.Item onClick={run(() => openDetail(row, 'edit'))}>{TABLE_TEXTS.edit}</Menu.Item>}
        {canManage && extraMenu?.(row, close)}
        {entries.includes('remove') && remove && (
          <>
            <Menu.Divider />
            <Menu.Item color="red" onClick={run(() => confirmThen(remove.question(row), (q) => window.confirm(q), () => remove.run(row)))}>
              {remove.label?.(row) ?? TABLE_TEXTS.remove}
            </Menu.Item>
          </>
        )}
      </>
    );
  }, { onClose: (row) => returnFocus(() => rowEls.current.get(rowKey(row)), () => !detailOpen.current) });

  const pick = (row: T, focus: boolean): void => {
    const key = rowKey(row);
    setSelected(key);
    if (focus) rowEls.current.get(key)?.focus();
  };
  const cancelPress = (): void => {
    if (press.current) clearTimeout(press.current.timer);
    press.current = null;
  };
  useEffect(() => cancelPress, []);

  const total = columns.reduce((sum, c) => sum + widthOf(c), 0) + MENU_COLUMN;
  const current = detail ? rows.find((r) => rowKey(r) === detail.key) : undefined;
  // A removed entry closes its popup.
  useEffect(() => { if (detail && !current) { detailOpen.current = false; setDetail(null); } }, [detail, current]);

  return (
    <>
      <Text size="xs" c="dimmed" mb={4}>{TABLE_TEXTS.hint}</Text>
      <Table.ScrollContainer minWidth={total} maxHeight={maxHeight}>
        <Table verticalSpacing={3} className="pst-tabular pst-libtable" highlightOnHover stickyHeader role="grid" aria-label={label} aria-rowcount={rows.length + 1} style={{ width: total }}>
          <Table.Thead>
            <Table.Tr>
              {columns.map((c) => (
                <Table.Th key={c.key} title={c.hint} ta={c.align} style={{ width: widthOf(c) }}>
                  {c.header ?? c.title}
                  <ColumnGrip title={c.title} width={widthOf(c)} min={c.minWidth ?? MIN_COLUMN_WIDTH} defaultWidth={c.defaultWidth} onChange={(w) => resize(c.key, w)} />
                </Table.Th>
              ))}
              <Table.Th style={{ width: MENU_COLUMN }} aria-label={TABLE_TEXTS.actions} />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {visible.map((row, i) => {
              const key = rowKey(row);
              const isSelected = key === selected;
              return (
                <Table.Tr
                  key={key}
                  ref={(el: HTMLTableRowElement | null) => { if (el) rowEls.current.set(key, el); else rowEls.current.delete(key); }}
                  aria-selected={isSelected}
                  aria-rowindex={i + 2}
                  tabIndex={key === tabRow ? 0 : -1}
                  style={rowStyle?.(row)}
                  onClick={() => setSelected(key)}
                  onDoubleClick={(e) => {
                    if ((e.target as HTMLElement).closest('input, button, a, textarea, select')) return;
                    window.getSelection()?.removeAllRanges();
                    openDetail(row, 'view');
                  }}
                  onContextMenu={(e) => { setSelected(key); menu.open(e, row); }}
                  onKeyDown={(e) => {
                    // Keys in the row's own controls (checkbox, ⋯) stay theirs.
                    if (e.target !== e.currentTarget) return;
                    const action = rowKeyAction(e);
                    if (!action) return;
                    e.preventDefault();
                    if (action === 'open') openDetail(row, 'view');
                    else if (action === 'menu') {
                      const r = e.currentTarget.getBoundingClientRect();
                      menu.openAt(r.left + 24, r.bottom, row);
                    } else {
                      const next = moveSelection(i, e.key, visible.length);
                      if (next !== null) pick(visible[next]!, true);
                    }
                  }}
                  onTouchStart={(e) => {
                    const t = e.touches[0];
                    if (!t || e.touches.length > 1) return;
                    cancelPress();
                    const at = { x: t.clientX, y: t.clientY };
                    press.current = { ...at, timer: setTimeout(() => { press.current = null; setSelected(key); menu.openAt(at.x, at.y, row); }, LONG_PRESS_MS) };
                  }}
                  onTouchMove={(e) => {
                    const t = e.touches[0];
                    if (press.current && t && movedTooFar(press.current, { x: t.clientX, y: t.clientY })) cancelPress();
                  }}
                  onTouchEnd={cancelPress}
                  onTouchCancel={cancelPress}
                >
                  {columns.map((c) => <Table.Td key={c.key} role="gridcell" ta={c.align}>{c.render(row)}</Table.Td>)}
                  <Table.Td role="gridcell" p={0}>
                    <ActionIcon
                      size="sm"
                      variant="subtle"
                      color="gray"
                      tabIndex={key === tabRow ? 0 : -1}
                      aria-label={`${TABLE_TEXTS.actions}: ${rowLabel(row)}`}
                      title={TABLE_TEXTS.actions}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelected(key);
                        const r = e.currentTarget.getBoundingClientRect();
                        menu.openAt(r.left, r.bottom, row);
                      }}
                      onDoubleClick={(e) => e.stopPropagation()}
                    >
                      ⋯
                    </ActionIcon>
                  </Table.Td>
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
        {lazy.more}
      </Table.ScrollContainer>
      {menu.menu}
      {detail && current && (
        <DetailModal
          key={detail.key}
          title={(detailTitle ?? rowLabel)(current)}
          fields={fields(current)}
          mode={detail.mode}
          canEdit={canManage && editable(current)}
          onEdit={() => setDetail({ key: detail.key, mode: 'edit' })}
          onCancelEdit={() => setDetail({ key: detail.key, mode: 'view' })}
          onSave={async (changes) => {
            if (Object.keys(changes).length > 0) await edit!(current, changes);
            setDetail({ key: detail.key, mode: 'view' });
          }}
          onClose={closeDetail}
        >
          {detailExtra?.(current)}
        </DetailModal>
      )}
    </>
  );
}

/**
 * The grip on a heading's right edge: dragged, by the arrow keys once it has
 * the focus, back to the default on a double click. The width follows the
 * pointer's total travel since the press (see resizedWidth).
 */
function ColumnGrip({ title, width, min, defaultWidth, onChange }: {
  title: string;
  width: number;
  min: number;
  defaultWidth: number;
  onChange: (width: number) => void;
}): JSX.Element {
  return (
    <div
      className="pst-col-grip"
      role="separator"
      aria-orientation="vertical"
      aria-label={`${TABLE_TEXTS.resize}: ${title}`}
      aria-valuenow={width}
      aria-valuemin={min}
      title={TABLE_TEXTS.resizeHint}
      tabIndex={0}
      onClick={(e) => e.stopPropagation()}
      onDoubleClick={(e) => { e.stopPropagation(); onChange(defaultWidth); }}
      onKeyDown={(e) => {
        const next = stepWidth(width, e.key, e.shiftKey, min);
        if (next === null) return;
        e.preventDefault();
        onChange(next);
      }}
      onPointerDown={(event) => {
        if (event.button !== 0) return;
        event.preventDefault();
        const origin = event.clientX;
        const base = width;
        const move = (moveEvent: PointerEvent): void => onChange(resizedWidth(base, origin, moveEvent.clientX, min));
        const stop = (): void => {
          window.removeEventListener('pointermove', move);
          window.removeEventListener('pointerup', stop);
          window.removeEventListener('pointercancel', stop);
          document.body.style.cursor = '';
          document.body.style.userSelect = '';
        };
        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';
        window.addEventListener('pointermove', move);
        window.addEventListener('pointerup', stop);
        window.addEventListener('pointercancel', stop);
      }}
    />
  );
}
